import { useQuery, gql, TypedDocumentNode } from '@apollo/client';
import React, { useMemo } from 'react';
import { Breakpoint } from '@moda/om';
import { useParams } from 'react-router-dom';
import { compact } from '@moda/portal-stanchions';
import { Loading } from '../../components/Loading';
import { LookviewPageQuery, LookviewPageQueryVariables } from '../../generated/types';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { useTrunkshowTitle, TRUNKSHOW_TITLE_FRAGMENT } from '../../hooks/useTrunkshowTitle';
import { useCountry } from '../../components/CountrySelector';
import { PageMeta } from '../../components/PageMeta';
import { NotFoundPage } from '../NotFoundPage';
import { MobileLookviewPage, MOBILE_LOOKVIEW_PAGE_FRAGMENT } from './MobileLookviewPage';
import { DesktopLookviewPage, DESKTOP_LOOKVIEW_PAGE_FRAGMENT } from './DesktopLookviewPage';

export const LOOKVIEW_PAGE_QUERY: TypedDocumentNode<LookviewPageQuery, LookviewPageQueryVariables> =
  gql`
    query LookviewPageQuery($slug: String!, $countryCode: String) {
      trunkshow(slug: $slug) {
        id
        ...TrunkshowTitleFragment
        looks(all: true) {
          edges {
            node {
              ...DesktopLookviewPageFragment
              ...MobileLookviewPageFragment
            }
          }
        }
      }
    }
    ${MOBILE_LOOKVIEW_PAGE_FRAGMENT}
    ${DESKTOP_LOOKVIEW_PAGE_FRAGMENT}
    ${TRUNKSHOW_TITLE_FRAGMENT}
  `;

export const LookviewPage: React.FC = () => {
  const { trunkshowSlug = '', lookId = '' } = useParams<{
    trunkshowSlug?: string;
    lookId?: string;
  }>();
  const {
    country: { alpha2Code: countryCode }
  } = useCountry();

  const { data, loading, error } = useQuery(LOOKVIEW_PAGE_QUERY, {
    variables: { slug: trunkshowSlug, countryCode }
  });

  const trunkshowTitle = useTrunkshowTitle(data?.trunkshow);

  if (error) {
    throwQueryError(error);
  }

  const looks = useMemo(
    () => compact(compact(data?.trunkshow?.looks?.edges).map(({ node }) => node)),
    [data]
  );

  if (!loading && (!data?.trunkshow || !data.trunkshow.looks)) return <NotFoundPage />;

  const look = looks.find(({ id }) => id === lookId);
  const lookText = look?.name ?? '';
  const title = `${trunkshowTitle} - ${lookText}`;
  const description = `Preorder the ${trunkshowTitle} on Moda Operandi.`;

  if (loading) return <Loading />;

  return (
    <>
      <PageMeta title={title} description={description} />

      <Breakpoint gt="sm">
        <DesktopLookviewPage looks={looks} />
      </Breakpoint>

      <Breakpoint lt="sm">
        <MobileLookviewPage looks={looks} />;
      </Breakpoint>
    </>
  );
};
